import { useLayoutEffect, useRef, useState } from 'react';
import { Icon, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import OrdersRowActions from 'app/main/apps/e-commerce/orders/OrdersRowActions';
import { staffListRowActionAllowed } from './staffListUi';
import { STAFF_LIST_TEST_IDS } from '../staffListTestIds';

const VISIBLE_ICONS = 3;

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function actionMeta(button) {
  const iconName = button.querySelector('.material-icons, .MuiIcon-root')?.textContent?.trim() || '';
  const raw = button.getAttribute('aria-label') || button.getAttribute('title') || iconName || 'Action';
  return {
    label: raw === iconName ? titleCase(raw) : raw,
    iconName,
    button,
  };
}

/**
 * Keeps the existing OrdersRowActions behaviour, but only shows three icons.
 * Anything after that stays in the same component and is opened from More.
 */
export default function StaffListRowActions(props) {
  const { typeValue, ...rowActionProps } = props;
  const rootRef = useRef(null);
  const [hidden, setHidden] = useState([]);
  const [anchor, setAnchor] = useState(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const collect = () => {
      const buttons = [...root.querySelectorAll('.MuiIconButton-root')].filter(
        (button) => !button.dataset.staffListMore
      );
      const visible = [];
      buttons.forEach((button) => {
        const meta = actionMeta(button);
        const allowed = staffListRowActionAllowed(props.typeValue, meta.label, props.listType);
        if (!allowed) {
          button.classList.add('staff-list-action-type-hidden');
          button.classList.remove('staff-list-action-overflow');
          button.dataset.staffListTypeHidden = '1';
          return;
        }
        button.classList.remove('staff-list-action-type-hidden');
        delete button.dataset.staffListTypeHidden;
        visible.push(button);
      });
      visible.forEach((button, index) => {
        button.classList.toggle('staff-list-action-overflow', index >= VISIBLE_ICONS);
      });
      setHidden(visible.slice(VISIBLE_ICONS).map(actionMeta));
    };

    collect();
    const observer = new MutationObserver(collect);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [props.row?.original?.id, props.row?.original?.status?.system_code, props.typeValue, props.listType]);

  return (
    <div ref={rootRef} className="staff-list-row-actions" data-testid={STAFF_LIST_TEST_IDS.rowActions}>
      <OrdersRowActions {...rowActionProps} />
      {hidden.length > 0 && (
        <>
          <Tooltip title="More actions">
            <IconButton
              data-staff-list-more="1"
              size="small"
              aria-label="More actions"
              onClick={(event) => setAnchor(event.currentTarget)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {hidden.map((item, index) => (
              <MenuItem
                key={`${item.label}-${index}`}
                onClick={() => {
                  setAnchor(null);
                  item.button.click();
                }}
                sx={{ gap: 1.25, minWidth: 180 }}
              >
                {item.iconName ? (
                  <Icon fontSize="small" sx={{ color: 'action.active' }}>{item.iconName}</Icon>
                ) : null}
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );
}
